import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingDown } from 'lucide-react';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useMrrChurn, useAnalyticsCurrency } from '@/hooks/useDashboardAnalytics';

interface MrrChurnWidgetProps {
  months?: number;
  className?: string;
}

interface ChartDatum {
  month: string;
  label: string;
  mrr: number;
  churnRate: number;
  activeCount: number;
}

function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  if (!year || !month) {return monthKey;}
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
}

interface TooltipPayloadItem {
  dataKey: string;
  value: number;
  color?: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  baseCurrency: string;
  data: ChartDatum[];
}

function ChartTooltip({ active, payload, label, baseCurrency, data }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) {return null;}
  const point = data.find((d) => d.label === label);
  if (!point) {return null;}
  const mrrFormatted = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: baseCurrency,
    maximumFractionDigits: 0,
  }).format(point.mrr);
  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">{point.label}</p>
      <p className="text-green-600">MRR: {mrrFormatted}</p>
      <p className="text-red-600">Churn: {point.churnRate}%</p>
      <p className="text-muted-foreground">Активных подписок: {point.activeCount}</p>
    </div>
  );
}

export function MrrChurnWidget({ months = 12, className }: MrrChurnWidgetProps) {
  const { data, isLoading } = useMrrChurn(months);
  const { data: currencyData } = useAnalyticsCurrency();
  const baseCurrency = currencyData?.currency ?? 'USD';

  const chartData = useMemo<ChartDatum[]>(() => {
    return (data?.months ?? []).map((p) => ({
      month: p.month,
      label: formatMonthLabel(p.month),
      mrr: p.mrr,
      churnRate: p.churnRate,
      activeCount: p.activeCount,
    }));
  }, [data?.months]);

  const compactFormatter = useMemo(() => {
    return new Intl.NumberFormat('ru-RU', { notation: 'compact', compactDisplay: 'short' });
  }, []);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            MRR и Churn
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[260px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (chartData.every((d) => d.mrr === 0 && d.activeCount === 0)) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            MRR и Churn
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Нет данных для расчёта MRR
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <TrendingDown className="h-4 w-4" />
          MRR и Churn
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Ежемесячная повторяющаяся выручка и уровень оттока
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="mrr"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => compactFormatter.format(Number(value))}
              width={50}
            />
            <YAxis
              yAxisId="churn"
              orientation="right"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => `${value}%`}
              width={40}
            />
            <Tooltip
              content={({ active, payload, label }) => (
                <ChartTooltip
                  active={active}
                  payload={payload as TooltipPayloadItem[] | undefined}
                  label={typeof label === 'string' ? label : undefined}
                  baseCurrency={baseCurrency}
                  data={chartData}
                />
              )}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              yAxisId="churn"
              dataKey="churnRate"
              name="Churn, %"
              fill="#ef4444"
              fillOpacity={0.5}
              radius={[2, 2, 0, 0]}
            />
            <Line
              yAxisId="mrr"
              type="monotone"
              dataKey="mrr"
              name={`MRR, ${baseCurrency}`}
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
